import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, ScanLine, RefreshCw, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api, errMsg } from '@/lib/api';
import { uploadFile } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/** Camera capture (mobile rear camera preferred). onCapture(File). */
const CameraCapture = ({ onCapture, onCancel, id }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let active = true;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => { if (!active) { s.getTracks().forEach((t) => t.stop()); return; } streamRef.current = s; if (videoRef.current) videoRef.current.srcObject = s; })
      .catch((e) => setErr(e?.message || 'Kamera tidak tersedia'));
    return () => { active = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);
  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob((b) => b && onCapture(new File([b], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' })), 'image/jpeg', 0.9);
  };
  if (err) return <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-800" data-testid="camera-error">{id ? 'Kamera tidak dapat diakses: ' : 'Camera unavailable: '}{err}. {id ? 'Gunakan tombol unggah.' : 'Use upload instead.'}</div>;
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl bg-black aspect-[3/4] sm:aspect-video"><video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="camera-video" /></div>
      <div className="flex justify-center gap-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={onCancel}><X size={16} className="mr-1.5" />{id ? 'Batal' : 'Cancel'}</Button>
        <Button type="button" className="rounded-xl" onClick={snap} data-testid="camera-snap"><Camera size={16} className="mr-1.5" />{id ? 'Ambil foto' : 'Capture'}</Button>
      </div>
    </div>
  );
};

/** Scan dialog: camera / upload → object storage → AI extraction → onResult(form, meta). */
export const BillScanDialog = ({ open, onClose, onResult, id }) => {
  const { company } = useAuth();
  const fileRef = useRef(null);
  const [mode, setMode] = useState('pick');
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const reset = () => { setMode('pick'); setPreview(null); setStatus(''); setBusy(false); };
  const close = () => { reset(); onClose(); };
  const process = async (file) => {
    setBusy(true);
    setMode('processing');
    if (file.type.startsWith('image/')) setPreview(URL.createObjectURL(file));
    try {
      setStatus(id ? 'Mengunggah dokumen…' : 'Uploading document…');
      const att = await uploadFile(company.id, file, { kind: 'bill' });
      setStatus(id ? 'AI sedang membaca tagihan…' : 'AI is reading the bill…');
      const { data } = await api.post(`/companies/${company.id}/purchasing/bills/scan`, { attachment_id: att.id });
      toast.success(id ? `Terbaca: ${data.form.lines.length} baris${data.matched_contact ? ', vendor cocok' : ''}` : `Read ${data.form.lines.length} lines`);
      onResult(data, att);
      reset();
    } catch (e) { toast.error(errMsg(e, e.message)); setMode('pick'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && close()}>
      <DialogContent className="sm:max-w-lg" data-testid="bill-scan-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ScanLine size={18} />{id ? 'Scan tagihan vendor' : 'Scan vendor bill'}</DialogTitle><DialogDescription>{id ? 'Foto struk/faktur atau unggah gambar/PDF. AI akan mengisi form tagihan otomatis — periksa sebelum menyimpan.' : 'Photograph the receipt/invoice or upload an image/PDF. AI pre-fills the bill form — review before saving.'}</DialogDescription></DialogHeader>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) process(f); }} data-testid="bill-scan-file-input" />
        {mode === 'pick' && (
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setMode('camera')} className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-6 hover:bg-muted/60 transition-colors" data-testid="bill-scan-camera"><Camera size={28} className="text-primary" /><span className="text-sm font-medium">{id ? 'Kamera' : 'Camera'}</span><span className="text-[11px] text-muted-foreground text-center">{id ? 'Foto langsung dari HP' : 'Shoot from your phone'}</span></button>
            <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-6 hover:bg-muted/60 transition-colors" data-testid="bill-scan-upload"><Upload size={28} className="text-primary" /><span className="text-sm font-medium">{id ? 'Unggah' : 'Upload'}</span><span className="text-[11px] text-muted-foreground text-center">JPG, PNG, WebP, PDF · 20 MB</span></button>
          </div>
        )}
        {mode === 'camera' && <CameraCapture id={id} onCancel={() => setMode('pick')} onCapture={process} />}
        {mode === 'processing' && (
          <div className="space-y-3">
            {preview && <div className="overflow-hidden rounded-2xl border border-border max-h-64 flex items-center justify-center bg-muted/40"><img src={preview} alt="" className="max-h-64 object-contain" /></div>}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2" data-testid="bill-scan-status"><Loader2 size={16} className="animate-spin text-primary" />{status}</div>
          </div>
        )}
        {mode === 'pick' && preview && <Button type="button" variant="ghost" size="sm" className="rounded-xl" onClick={() => setPreview(null)}><RefreshCw size={14} className="mr-1" />{id ? 'Ulangi' : 'Retry'}</Button>}
      </DialogContent>
    </Dialog>
  );
};
